import { useState } from "react";

/**
 * PinKeypad — Teclado numérico touchscreen reutilizável.
 * Props:
 *  - title, subtitle: textos do topo
 *  - expectedPin: PIN correto (4 dígitos) — validado internamente
 *  - onSuccess: callback quando PIN correto
 *  - onCancel: callback para fechar/voltar (opcional)
 *  - primaryColor: cor de destaque (do tema do tenant)
 *  - secondaryActionLabel + onSecondaryAction: botão alternativo (ex: "Continuar Operação")
 */
const PinKeypad = ({
    title = "Insira o PIN",
    subtitle = "4 dígitos numéricos",
    expectedPin,
    onSuccess,
    onCancel,
    primaryColor = "#d97706",
    secondaryActionLabel,
    onSecondaryAction,
    testId = "pin-keypad",
}) => {
    const [pin, setPin] = useState("");
    const [shaking, setShaking] = useState(false);

    const handleDigit = (d) => {
        if (pin.length >= 4) return;
        const next = pin + d;
        setPin(next);
        if (next.length === 4) {
            setTimeout(() => {
                if (next === expectedPin) {
                    onSuccess?.();
                    setPin("");
                } else {
                    setShaking(true);
                    setTimeout(() => {
                        setShaking(false);
                        setPin("");
                    }, 600);
                }
            }, 220);
        }
    };

    const handleBack = () => setPin((p) => p.slice(0, -1));
    const handleClear = () => setPin("");

    return (
        <div
            data-testid={testId}
            className={`relative w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/90 backdrop-blur-xl p-8 md:p-10 shadow-2xl ${
                shaking ? "animate-[pinshake_0.4s]" : ""
            }`}
        >
            <div className="flex flex-col items-center text-center">
                <div
                    className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5"
                    style={{ background: `${primaryColor}22`, color: primaryColor }}
                >
                    <i className="fa-solid fa-lock text-2xl"></i>
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Acesso protegido
                </div>
                <h2 className="salon-heading text-2xl text-white mt-1">{title}</h2>
                <p className="text-xs text-slate-400 mt-2">{subtitle}</p>
            </div>

            <div className="mt-7 flex justify-center gap-3">
                {[0, 1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className="h-14 w-12 rounded-xl border border-white/10 bg-slate-950/60 flex items-center justify-center text-2xl font-bold text-white"
                        style={pin[i] ? { borderColor: primaryColor, color: primaryColor } : {}}
                    >
                        {pin[i] ? "•" : ""}
                    </div>
                ))}
            </div>

            <div className="mt-7 grid grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                    <button
                        key={n}
                        data-testid={`${testId}-key-${n}`}
                        onClick={() => handleDigit(String(n))}
                        className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 active:scale-95 border border-white/5 text-white text-xl font-semibold transition-all"
                    >
                        {n}
                    </button>
                ))}
                <button
                    data-testid={`${testId}-key-clear`}
                    onClick={handleClear}
                    className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-white/5 text-slate-400 text-xs font-semibold uppercase tracking-wider transition-all"
                >
                    Limpar
                </button>
                <button
                    data-testid={`${testId}-key-0`}
                    onClick={() => handleDigit("0")}
                    className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 active:scale-95 border border-white/5 text-white text-xl font-semibold transition-all"
                >
                    0
                </button>
                <button
                    data-testid={`${testId}-key-back`}
                    onClick={handleBack}
                    className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-white/5 text-slate-400 transition-all"
                >
                    <i className="fa-solid fa-delete-left"></i>
                </button>
            </div>

            {(secondaryActionLabel || onCancel) && (
                <div className="mt-5 flex items-center gap-2">
                    {secondaryActionLabel && (
                        <button
                            data-testid={`${testId}-secondary`}
                            onClick={onSecondaryAction}
                            className="flex-1 min-h-[48px] rounded-xl border border-white/10 hover:border-white/30 text-slate-300 hover:text-white text-xs uppercase tracking-wider transition-all"
                        >
                            {secondaryActionLabel}
                        </button>
                    )}
                    {onCancel && (
                        <button
                            data-testid={`${testId}-cancel`}
                            onClick={onCancel}
                            className="min-h-[48px] px-4 rounded-xl border border-white/10 hover:border-red-500/30 text-slate-400 hover:text-red-400 text-xs uppercase tracking-wider transition-all"
                        >
                            Cancelar
                        </button>
                    )}
                </div>
            )}

            <style>{`@keyframes pinshake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
        </div>
    );
};

export default PinKeypad;
